import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Dialog, DialogActions, DialogContent, MenuItem, Stack } from '@mui/material';
import React, { FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
    ControlledSelect,
    DialogTitleWithClose,
    IUserRoles,
    IUserRolesForm,
    MultipleSelectComponent,
    useOrganisations,
} from '../../../shared';
import { useUserRoleSchema } from '../../validators';
import { useSaveUserRole } from '../../hooks/user-role.hook';
import { useRoleList } from '../../hooks/role.hook';

interface Props {
    userId: string;
    allUserRoles: IUserRoles[];
    open: boolean;
    handleClose: () => void;
    userRolesOrganisation?: IUserRoles;
}

export const UserRolesModal: FC<Props> = ({ open, handleClose, userId, allUserRoles, userRolesOrganisation }) => {
    const { t } = useTranslation();
    const organisations = useOrganisations();
    const { data: roles } = useRoleList({ page: 1, limit: 100 }, { onGetErrorMessage: t('getRolesError') });
    const { mutateAsync: saveUserRoles, isPending } = useSaveUserRole();

    const form = useForm<IUserRolesForm>({
        resolver: yupResolver(useUserRoleSchema()),
        mode: 'all',
    });

    useEffect(() => {
        if (userRolesOrganisation) {
            form.reset({
                id: userRolesOrganisation.id,
                organisationId: userRolesOrganisation.context?.key,
                roles: userRolesOrganisation.roles?.map((role) => role.id),
            });
        }
    }, [form, userRolesOrganisation]);

    const onSubmit = (data: IUserRolesForm) => {
        saveUserRoles({
            data: {
                id: data.id,
                subject: { user: { id: userId } },
                context: { key: data.organisationId },
                roles: data.roles,
            },
            messages: {
                onCreateSuccessMessage: t('createUserRolesSuccess'),
                onCreateErrorMessage: t('createUserRolesError'),
                onUpdateSuccessMessage: t('updateUserRolesSuccess'),
                onUpdateErrorMessage: t('updateUserRolesError'),
            },
        });
        handleClose();
    };

    const filteredOrganisations = useMemo(() => {
        return organisations.filter(
            (org) =>
                userRolesOrganisation?.context?.key === org.id ||
                !allUserRoles?.some((userRole) => userRole.context?.key === org.id),
        );
    }, [organisations, allUserRoles, userRolesOrganisation]);

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <FormProvider {...form}>
                <form noValidate>
                    <DialogTitleWithClose onClose={handleClose}>{t('addUserRoles')}</DialogTitleWithClose>
                    <DialogContent>
                        <Stack spacing={2} mt={2}>
                            <ControlledSelect name="organisationId" label={t('organisation')} required>
                                {filteredOrganisations.map((organisation) => (
                                    <MenuItem key={organisation.id} value={organisation.id}>
                                        {organisation.name}
                                    </MenuItem>
                                ))}
                            </ControlledSelect>
                            <MultipleSelectComponent
                                name="roles"
                                label={t('roles')}
                                items={roles?.items.map((item) => ({ id: item.id, name: item.key })) || []}
                                required
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="contained"
                            color="primary"
                            id="submit-user-roles"
                            disabled={isPending}
                            onClick={form.handleSubmit((data) => onSubmit(data))}
                        >
                            {t('save')}
                        </Button>
                        <Button onClick={handleClose} disabled={isPending} color="secondary">
                            {t('cancel')}
                        </Button>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};
